import PageShell from "@/components/feature/PageShell";
import ScrollReveal from "@/components/feature/ScrollReveal";
import { Link } from "react-router-dom";
import { useModal } from "@/hooks/useModal";

const offerings = [
  {
    icon: "ri-search-line",
    title: "IT Governance & Compliance Review",
    desc: "Deep-dive assessment of your governance frameworks. We ensure your technology policies meet regulatory and industry standards.",
    items: ["IT governance & compliance review", "Cloud cost optimisation & architecture audit", "Vendor selection & contract negotiation", "Disaster recovery & business continuity"],
  },
  {
    icon: "ri-cloudy-line",
    title: "Cloud Cost Optimisation & Architecture Audit",
    desc: "Move to the cloud the right way. We plan, execute, and optimize cloud migrations with minimal disruption to your operations.",
    items: ["Multi-cloud strategy", "Lift-and-shift vs re-architecture", "Cost governance & FinOps", "Cloud cost optimisation & architecture audit"],
  },
  {
    icon: "ri-refresh-line",
    title: "Vendor Selection & Contract Negotiation",
    desc: "Modernize your business processes with technology. From paper-based workflows to fully digitized operations.",
    items: ["Process automation", "Workflow digitization", "Change management support", "Vendor selection & contract negotiation"],
  },
  {
    icon: "ri-database-2-line",
    title: "Disaster Recovery & Business Continuity",
    desc: "Prepare for the worst. We design and test backup, recovery, and continuity plans that keep your business running.",
    items: ["RTO/RPO planning", "Backup strategy design", "Recovery testing & drills", "Disaster recovery & business continuity"],
  },
];

const approach = [
  { step: "1", title: "Discover", desc: "Understand your current state, goals, and constraints through stakeholder interviews and system analysis." },
  { step: "2", title: "Assess", desc: "Identify gaps, risks, and opportunities. Build a prioritized roadmap with clear milestones." },
  { step: "3", title: "Deliver", desc: "Execute the roadmap with hands-on support. Transfer knowledge and document everything." },
];

export default function ConsultingPage() {
  const { openQuote } = useModal();

  return (
    <PageShell>
      <section className="relative w-full pt-28 md:pt-36 pb-16 md:pb-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8 mb-8">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-brand-500 transition-colors">
            <i className="ri-arrow-left-line" /> Back to Services
          </Link>
        </div>
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                IT Consulting
              </span>
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
                Strategic IT Advisory
              </h1>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                We align technology with business outcomes. From infrastructure health checks to digital transformation strategy.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {offerings.map((o, i) => (
              <ScrollReveal key={o.title} delay={i + 1}>
                <div className="group p-6 md:p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border hover:border-brand-200 dark:hover:border-brand-500/30 transition-all duration-300 h-full">
                  <div className="w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${o.icon} text-xl text-brand-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {o.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                    {o.desc}
                  </p>
                  <ul className="space-y-2">
                    {o.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                        <i className="ri-check-line text-brand-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-white dark:bg-dark-bg">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                Our Approach
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                How We Work With You
              </h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
            {approach.map((step, i) => (
              <ScrollReveal key={step.step} delay={i + 1}>
                <div className="text-center p-6 md:p-8 rounded-2xl bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border">
                  <div className="w-12 h-12 rounded-full bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center mx-auto mb-4">
                    <span className="text-lg font-bold text-brand-500">{step.step}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-brand-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Let Us Guide Your IT Strategy
          </h2>
          <p className="text-sm md:text-base text-brand-100 max-w-lg mx-auto mb-8">
            Schedule a free consultation and get expert advice tailored to your organisation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={openQuote}
              className="px-8 py-3.5 rounded-full bg-white text-brand-600 text-sm font-semibold hover:bg-brand-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
            >
              Book Consultation
            </button>
            <Link
              to="/services"
              className="px-8 py-3.5 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold hover:bg-white/20 transition-all border border-white/20 whitespace-nowrap text-center cursor-pointer"
            >
              All Services
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}